import { UsageHelperBanner } from "@/components/shared/banners/usage-helper-banner";
import { DocumentSplitDropzone } from "@/components/shared/dropzones/document-split-dropzone";
import { DocumentUploadDropzone } from "@/components/shared/dropzones/document-upload-dropzone";
import { Button } from "@/components/ui/button";
import { InvoiceOverviewTable } from "@/features/invoice-overview/invoiceOverviewTable";
import { useUploadsInitialQuery } from "@/features/todos/initial-filters";
import { useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import {
  InvoiceType,
  type InvoiceTypeString,
} from "@/lib/constants/invoice-type";
import { cn } from "@/lib/utils";
import { InvoicesProvider } from "@/providers/invoices";
import { useTenant } from "@/providers/session";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import type { InvoiceFilterFields } from "@/types/utils";
import { File, Pencil, Upload } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DebtorsWarningBanner } from "./banners/debtors-warning-banner";
import { useDebtorsWarning } from "./banners/use-debtors-warning";

export default function TodosUploads({ type }: { type: InvoiceTypeString }) {
  const storageKey = useMemo(() => {
    return `todos-uploads-${type}`;
  }, [type]);

  const invoiceEnumType = useMemo(() => {
    return {
      payable: InvoiceType.PAYABLE,
      receivable: InvoiceType.RECEIVABLE,
    }[type];
  }, [type]);

  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const { query } = useUploadsInitialQuery({ type });
  const { page, setPage, limit, setLimit } = useTablePagination();
  const {
    globalSearch,
    setGlobalSearch,
    filters,
    serverSideFilters,
    setFilters,
    sorting,
    onSortingChange,
    sort,
    order,
    initialServerSideFilters,
    resetFilters,
  } = useServerSideQuery<InvoiceFilterFields>({
    storageKey,
    defaultFilters: query,
  });

  const columnVisibility = useMemo(
    () => ({
      type: false,
      invoiceNo: true,
      invoiceDate: true,
      startDate: true,
      expenseAccount: true,
      datevBu: true,
      tags: false,
      createdAt: true,
      status: true,
      reviewType: false,
      ocrReviewed: false,
      isBooking: false,
      isDuplicate: false,
    }),
    [],
  );

  const actionsConfig = useMemo(
    () => ({
      showStatusUpdateButton: false,
      showTypeUpdateButton: false,
      exportButton: {
        isVisible: false,
      },
    }),
    [],
  );

  const {
    data: invoices,
    error,
    loading,
    pagination,
  } = useGetAllInvoices({
    query: serverSideFilters,
    page,
    sort,
    order,
    limit,
  });

  const { showDebtorsWarning } = useDebtorsWarning(type);

  return (
    <div className="flex flex-col h-full gap-4">
      {showDebtorsWarning ? (
        <DebtorsWarningBanner />
      ) : (
        <UsageHelperBanner
          label={t("todos.uploads_explainer")}
          link="https://tabula-accounting.notion.site/Prozessleitlinie-DATEV-Unternehmen-Online-mit-Tabular-verwenden-256eecb29d04804fb21dd295ecdd76b0?pvs=74"
          linkText={t("ai_processing.how_to_download")}
        />
      )}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="md:col-span-3">
          <DocumentUploadDropzone
            tenantId={tenantId}
            clickUploadEnabled
            invoiceType={invoiceEnumType}
          >
            <div className="flex items-center justify-center w-full">
              <div className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Upload className="w-5 h-5 text-gray-700" />
                      <span className="text-gray-700 font-medium text-sm">
                        Hier hochladen
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">oder</span>
                    <Button className="h-10 px-4 py-2 text-sm font-medium gap-2">
                      <File className="h-4 w-4" />
                      {t("select_files")}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("pdf_jpg_png_xml")}
                  </p>
                </div>
              </div>
            </div>
          </DocumentUploadDropzone>
        </div>

        <div className="md:col-span-1">
          <DocumentSplitDropzone clickUploadEnabled>
            {({ error }) => (
              <div className="flex flex-col gap-1 items-center justify-center w-full min-w-[300px] relative">
                <div
                  className={cn(
                    "flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600",
                    error && "border-destructive bg-destructive/10",
                  )}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 px-4">
                    <div className="mb-2 flex items-center gap-2">
                      <Pencil className="w-5 h-5 text-gray-700" />
                      <span className="text-sm text-gray-700 font-medium">
                        {t("documents_organize")}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      <span className="font-semibold">
                        {t("click_to_upload")}
                      </span>
                      {t("documents_upload_to_organize")}
                    </p>
                  </div>
                </div>
                {error && (
                  <p className="text-xs text-destructive text-center">
                    {error}
                  </p>
                )}
              </div>
            )}
          </DocumentSplitDropzone>
        </div>
      </div>
      <InvoicesProvider invoices={invoices} loading={loading}>
        <InvoiceOverviewTable
          tableName="uploads"
          enableFilters={false}
          columnVisibility={columnVisibility}
          actionsConfig={actionsConfig}
          totalItems={pagination.totalItems}
          currentPage={page}
          totalPages={pagination.totalPages}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          filters={filters}
          setFilters={setFilters}
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          sorting={sorting}
          onSortingChange={onSortingChange}
          error={error}
          invoiceType={type}
          initialServerSideFilters={initialServerSideFilters}
          globalServerSideSearch={globalSearch}
          serverSideFilters={serverSideFilters}
          resetFilters={resetFilters}
        />
      </InvoicesProvider>
    </div>
  );
}
